import { useState, useEffect } from "react"
import { BASE_URL } from '../../apiConfig'
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner'
import Loading from '../../cors/Loading'
import { useParams } from 'react-router-dom'
import BankSelect from '../../pages/auth/components/BankSelect';
import SallerBank from "../../cors/SallerBank";
import ChangePassword from "../../cors/ChangePassword";

export default function SellerProfile() {
  const { token, role, roles } = useAuth();
  const [isEditing, setIsEditing] = useState(false)
  const [sellerData, setSellerData] = useState(null)
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [logo, setLogo] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [errors, setErrors] = useState({})

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    name_store: '',
    nameBank: '',
    rib: '',
    address: '',
    phone: '',
    level: '',
  })

  const fetchSellerData = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/getsaller/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      })
      
      if (!response.ok) {
        throw new Error('Échec du chargement des données du vendeur')
      }
      
      const data = await response.json()
      
      setSellerData(data.data)
      setFormData({
        name: data.data.name,
        email: data.data.email,
        name_store: data.data.info.name_store,
        nameBank: data.data.info.name_bank,
        rib: data.data.info.rib,
        address: data.data.info.address,
        phone: data.data.info.phone,
        level: data.data.info.level,
      })
    } catch (err) {
      setError(err.message)
      toast.error("Erreur lors du chargement des données")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSellerData()
  }, [id, token])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setLogo(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const clearLogo = () => {
    setLogo(null)
    setLogoPreview(null)
  }

  const handleSave = async () => {
    setUpdateLoading(true);
    try {
      const formDataToSend = new FormData()
      formDataToSend.append('user_id', id)
      formDataToSend.append('name', formData.name)
      formDataToSend.append('email', formData.email)
      formDataToSend.append('name_store', formData.name_store)
      formDataToSend.append('name_bank', formData.nameBank)
      formDataToSend.append('rib', formData.rib)
      formDataToSend.append('address', formData.address)
      formDataToSend.append('phone', formData.phone)
      formDataToSend.append('level', formData.level)
      
      if (logo) {
        formDataToSend.append('logo', logo)
      }

      const response = await fetch(`${BASE_URL}/api/update-saller`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        body: formDataToSend,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Échec de la mise à jour du profil")
      }

      toast.success("Profil mis à jour avec succès!");
      fetchSellerData();
      setIsEditing(false);
      setUpdateLoading(false);
    } catch (err) {
      toast.error(err.message)
    }
  }

  const handleCancel = () => {
    if (sellerData) {
      setFormData({
        name: sellerData.name,
        email: sellerData.email,
        name_store: sellerData.info.name_store,
        nameBank: sellerData.info.name_bank,
        rib: sellerData.info.rib,
        address: sellerData.info.address,
        phone: sellerData.info.phone,
        level: sellerData.info.level,
      })
    }
    setIsEditing(false)
    setLogo(null)
    setLogoPreview(null)
  }

  if (loading) {
    return (
    <div className="max-w-5xl mx-auto min-h-screen py-8 px-4 animate-pulse">
      <div className="mx-auto">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                {/* Logo Skeleton */}
                <div className="w-16 h-16 md:w-32 md:h-32 bg-gray-300 rounded-full border-4 border-white shadow-lg"></div>
                <div className="space-y-2">
                  <div className="h-6 bg-gray-200/50 rounded w-48"></div>
                  <div className="h-4 bg-gray-200/50 rounded w-32"></div>
                  <div className="h-3 bg-gray-200/50 rounded w-40"></div>
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                  <div className="h-8 bg-gray-200/50 rounded w-16 mx-auto"></div>
                  <div className="h-3 bg-gray-200/50 rounded w-20 mx-auto mt-2"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Stats Skeleton */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-8 bg-gray-200 rounded w-24 mx-auto"></div>
                  <div className="h-4 bg-gray-200 rounded w-28 mx-auto mt-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-20 mx-auto mt-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Profile Information Skeleton */}
        <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8">
          {/* Edit Button Skeleton */}
          <div className="flex justify-end py-5">
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
          
          {/* Form Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Personal Information Skeleton */}
            <div className="max-xl:px-4 p-8">
              <div className="flex items-center mb-6">
                <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
                <div className="h-6 bg-gray-200 rounded w-48"></div>
              </div>

              <div className="space-y-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i}>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-12 bg-gray-100 rounded-2xl"></div>
                  </div>
                ))}
                {/* Logo Upload Skeleton */}
                <div>
                  <div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
                  <div className="h-12 bg-gray-100 rounded-xl"></div>
                </div>
              </div>
            </div>

            {/* Business Information Skeleton */}
            <div className="p-8">
              <div className="flex items-center mb-6">
                <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
                <div className="h-6 bg-gray-200 rounded w-48"></div>
              </div>

              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i}>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-12 bg-gray-100 rounded-2xl"></div>
                  </div>
                ))}
                {/* Bank Select Skeleton */}
                <div>
                  <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
                  <div className="h-12 bg-gray-100 rounded-2xl"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Password Section Skeleton */}
        <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8 mt-8">
          <div className="flex items-center mb-6">
            <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
            <div className="h-6 bg-gray-200 rounded w-40"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-12 bg-gray-100 rounded-2xl"></div>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-8">
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
        </div>
      </div>
    </div>
    )
  }

  if (error) {
    return <div className='flex justify-center mt-70'><div className="text-red-500 p-4">Erreur: {error}</div></div>
  }

  if (!sellerData) {
    return <div className='flex justify-center mt-70'><div className="p-4">Aucune donnée de vendeur trouvée</div></div> 
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  return (
    <div className="max-w-5xl mx-auto min-h-screen py-8 px-4">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                {sellerData.info.logo ?
                  <img 
                    src={`${BASE_URL}/storage/${sellerData.info.logo}`}
                    width={50}
                    height={50}
                    className="w-16 h-16 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg object-cover"
                    alt="logo" />
                  :
                  <div className="w-16 h-16 md:w-32 md:h-32 bg-[var(--color-primary-50)] rounded-full border-4 border-white shadow-lg flex items-center justify-center ">
                    <svg className="h-10 w-10 md:w-16 md:h-16 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                }
                <div className="text-white">
                  <h1 className="text-xl md:text-2xl font-bold">{sellerData.name}</h1>
                  <p className="text-emerald-100 text-xs">Store : {sellerData.info.name_store}</p>
                  <p className="text-xs text-emerald-100 mt-1">{sellerData.email}</p>
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                  <div className="text-2xl font-bold ">{sellerData.info.level === "Beginner" ? 1 : sellerData.info.level === "Intermediate" ? 2 :3},0★</div>
                  <div className="text-xs text-emerald-500">Trust Score</div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Stats */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatMoney(sellerData.total_profite)} MAD</div>
                <div className="text-sm text-gray-600">Total profite</div>
                <div className="text-xs text-green-600 mt-1">↗ +23%</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{sellerData.total_orders}</div>
                <div className="text-sm text-gray-600">Total ventes</div>
                <div className="text-xs text-green-600 mt-1">↗ +15%</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{sellerData.total_orders_livre}</div>
                <div className="text-sm text-gray-600">Commandes livré</div>
                <div className="text-xs text-green-600 mt-1">↗ +2.1%</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{sellerData.total_orders_encours}</div>
                <div className="text-sm text-gray-600">En livraison</div>
                <div className="text-xs text-green-600 mt-1">↗ +18%</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Profile Information */}
        <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8">
          {
            (role === 1 || (role === 2 && roles.edit_saller === 1)) &&
              <div className="flex justify-end py-5 space-x-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold  transition-colors duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span className="text-sm max-xl:text-xs">Edit Profile</span>
                  </button>
                ) : (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSave}
                      disabled={updateLoading}
                      className={`bg-green-500 cursor-pointer text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors duration-200 flex items-center space-x-2 ${passwordLoading ? 'opacity-70' : ''}`}
                    >
                      {updateLoading ? (
                          <>
                            <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span className="max-xl:text-xs">Mise à jour...</span>
                          </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm max-xl:text-xs">Mettre à Jour</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm max-xl:text-xs">Cancel</span>
                    </button>
                  </div>
                )}
              </div>
          }
          
          {/* Personal Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="max-xl:px-4 p-8">
              <div className="flex items-center mb-6">
                <div className="bg-[var(--color-primary-50)] p-3 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Personal Information</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{sellerData.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Email Address</label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{sellerData.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Phone Number</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{sellerData.info.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Address</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{sellerData.info.address}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Store Logo (Optional)</label>
                  {isEditing ? (
                    <div className="relative border border-gray-200 rounded-xl bg-gray-50 focus-within:ring-2 focus-within:ring-[var(--color-primary-200)]">
                      <input
                        type="file"
                        id="logo"
                        name="logo"
                        onChange={handleLogoChange}
                        className="w-full cursor-pointer text-xs pr-11 pl-4 py-3 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-primary-50)] file:text-[var(--color-primary-500)] hover:file:bg-[var(--color-primary-100)]"
                        accept="image/*"
                      />
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  ) : (
                    sellerData.info.logo ? (
                      <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="flex items-center">
                          <img 
                            src={`${BASE_URL}/storage/${sellerData.info.logo}`} 
                            alt="Logo Preview" 
                            className="h-16 w-16 object-contain rounded-lg mr-4" 
                          />
                          <span className="text-sm text-gray-700">Current Logo</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-xs italic">No logo uploaded</p>
                    )
                  )}

                  {logoPreview && !sellerData.info.logo && (
                    <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                      <div className="flex items-center">
                        <img src={logoPreview} alt="Logo Preview" className="h-16 w-16 object-contain rounded-lg mr-4" />
                        <span className="text-sm text-gray-700">{logo ? logo.name : ''}</span>
                      </div>
                      <button
                        type="button"
                        onClick={clearLogo}
                        className="text-red-500 cursor-pointer hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition-colors"
                        aria-label="Remove logo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="p-8">
              <div className="flex items-center mb-6">
                <div className="bg-[var(--color-primary-500)]/15 p-3 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Business Information</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Store Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name_store"
                      value={formData.name_store}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{sellerData.info.name_store}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Seller Level</label>
                  {isEditing ? (
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{sellerData.info.level}</p>
                  )}
                </div>


                 <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Bank Name</label>
                  {isEditing ? (
                    <BankSelect 
                      type={false}
                      formData={formData} 
                      handleChange={handleInputChange} 
                      errors={errors}
                    />
                  ) : (
                    <div className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">  
                      <SallerBank bank={sellerData.info.name_bank} />  
                    </div>
                  )}
                </div> 

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">RIB</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="rib"
                      value={formData.rib}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl font-mono">
                      {sellerData.info.rib}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Password Section */}
        {
          (role === 1 || (role === 2 && roles.change_saller_password === 1)) &&
            <ChangePassword userId={id} />  
        }
      </div>
    </div>
  )
}